import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { IoCheckmarkCircle, IoRefreshOutline, IoAlertCircleOutline } from 'react-icons/io5';
import { setPackagingUnitConformityAssessments, fetchConformityMeta } from '../../../../store/actions/packagingUnit';
import { successGlobal } from '../../../../store/reducers/notifications';
import { BASE_LANGUAGE, getMarketLanguages } from '../../../../utils/constants/marketLanguages';
import LanguageSwitch from '../languageSwitch';

const Chevron = ({ open }) => (
    <svg
        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
    >
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const formatHistoryDate = (value) => new Date(value).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });

const HistoryEntry = ({ entry }) => {
    const isCompleted = entry.event === 'COMPLETED';
    const isAuto = entry.source === 'AUTO_INVALIDATED';
    const Icon = isCompleted ? IoCheckmarkCircle : IoRefreshOutline;
    const iconColor = isCompleted ? '#198754' : '#C97D2C';

    return (
        <div className="d-flex gap-2 py-2" style={{ borderBottom: '1px solid #EEF0F8' }}>
            <Icon size={16} style={{ color: iconColor, flexShrink: 0, marginTop: 2 }} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: iconColor }}>
                    {isCompleted ? 'Posúdenie zhody splnené' : 'Posúdenie zhody prestalo byť splnené'}
                </div>
                <div className="text-muted" style={{ fontSize: 11.5 }}>
                    {formatHistoryDate(entry.createdAt)}
                    {entry.changedByName && <> · {entry.changedByName}</>}
                    {isAuto && <> · automaticky</>}
                </div>
                {entry.note && (
                    <div className="text-muted fst-italic mt-1" style={{ fontSize: 12 }}>{entry.note}</div>
                )}
            </div>
        </div>
    );
};

// Server je zdrojom pravdy - unit.conformitySummary.criteria už obsahuje pre každé kritérium
// jeho text, či je pre danú jednotku relevantné (visible) a aktuálny uložený stav/poznámku.
const buildInitialDrafts = (criteria) => Object.fromEntries(
    criteria.map(c => [c.id, { status: c.status, note: c.note, assessmentType: c.assessmentType || '', translations: c.translations || {} }])
);

const ConformityAssessmentTab = ({ unit, description }) => {
    const dispatch = useDispatch();
    const statusOptions = useSelector(state => state.packagingUnit.conformityStatusOptions);
    const summary = unit.conformitySummary ?? { criteria: [], complete: true, requiredCount: 0, fulfilledCount: 0 };
    const visibleCriteria = summary.criteria.filter(c => c.visible);
    const history = [...(unit.conformityHistory || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const [drafts, setDrafts] = useState(() => buildInitialDrafts(visibleCriteria));
    const [expanded, setExpanded] = useState({});
    const [saving, setSaving] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [textLang, setTextLang] = useState(BASE_LANGUAGE);

    const marketLanguages = useMemo(() => getMarketLanguages(unit.marketCountries), [unit.marketCountries]);
    const activeLang = marketLanguages.includes(textLang) ? textLang : BASE_LANGUAGE;

    useEffect(() => {
        if (statusOptions.length === 0) dispatch(fetchConformityMeta());
    }, [dispatch, statusOptions.length]);

    const savedDrafts = buildInitialDrafts(visibleCriteria);
    const isDirty = visibleCriteria.some(c => (
        drafts[c.id] && (
            drafts[c.id].status !== savedDrafts[c.id].status ||
            drafts[c.id].note !== savedDrafts[c.id].note ||
            drafts[c.id].assessmentType !== savedDrafts[c.id].assessmentType ||
            JSON.stringify(drafts[c.id].translations) !== JSON.stringify(savedDrafts[c.id].translations)
        )
    ));

    const getNoteForLang = (criterionId) => {
        const draft = drafts[criterionId] ?? savedDrafts[criterionId];
        return activeLang === BASE_LANGUAGE ? draft.note : (draft.translations?.[activeLang]?.note ?? '');
    };

    const setNoteForLang = (criterionId, value) => {
        setDrafts(d => {
            const draft = d[criterionId] ?? savedDrafts[criterionId];
            if (activeLang === BASE_LANGUAGE) {
                return { ...d, [criterionId]: { ...draft, note: value } };
            }
            return {
                ...d,
                [criterionId]: {
                    ...draft,
                    translations: { ...draft.translations, [activeLang]: { ...draft.translations?.[activeLang], note: value } },
                },
            };
        });
    };

    const toggleExpanded = (criterionId) => setExpanded(e => ({ ...e, [criterionId]: !e[criterionId] }));

    const updateDraft = (criterionId, field, value) => {
        setDrafts(d => ({ ...d, [criterionId]: { ...d[criterionId], [field]: value } }));
    };

    const handleSaveAll = () => {
        setSaving(true);
        const entries = visibleCriteria.map(c => {
            const draft = drafts[c.id] ?? savedDrafts[c.id];
            return { criterionId: c.id, status: draft.status, note: draft.note, assessmentType: draft.assessmentType, translations: draft.translations };
        });
        dispatch(setPackagingUnitConformityAssessments({ packagingUnitId: unit._id, entries }))
            .unwrap()
            .then(() => dispatch(successGlobal('Posúdenie zhody bolo úspešne uložené.')))
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    return (
        <div>
            {description && (
                <p className="text-muted small mb-3" style={{ maxWidth: 720 }}>{description}</p>
            )}

            <div
                className="d-flex align-items-center gap-3 mb-4"
                style={{
                    backgroundColor: summary.complete ? '#E6F4EA' : '#FBEBD8',
                    border: `1px solid ${summary.complete ? '#8FCBA6' : '#C97D2C'}`,
                    borderRadius: 12,
                    padding: '14px 18px',
                }}
            >
                {summary.complete
                    ? <IoCheckmarkCircle size={28} style={{ color: '#198754', flexShrink: 0 }} />
                    : <IoAlertCircleOutline size={28} style={{ color: '#C97D2C', flexShrink: 0 }} />}
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: summary.complete ? '#198754' : '#C97D2C' }}>
                        Posúdenie zhody: {summary.complete ? 'Splnené' : 'Nesplnené'}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12.5 }}>
                        {summary.fulfilledCount}/{summary.requiredCount} povinných kritérií splnených
                    </div>
                </div>
            </div>

            <div className="fw-semibold mb-2" style={{ color: '#0B1157', fontSize: 14 }}>
                Rozpis podľa kritérií
            </div>

            <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8' }}>
                <table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: '#F4F5FE' }}>
                            <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', minWidth: 320 }}>Kritérium</th>
                            <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', minWidth: 200 }}>Vyhodnotenie</th>
                            <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', minWidth: 130 }}>Hodnotenie</th>
                            <th style={{ padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', minWidth: 200 }}>
                            <div className="d-flex align-items-center gap-2">
                                Poznámka / odôvodnenie
                                {marketLanguages.length > 0 && (
                                    <LanguageSwitch languages={marketLanguages} active={activeLang} onChange={setTextLang} size="sm" />
                                )}
                            </div>
                        </th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleCriteria.map((c, i) => {
                            const draft = drafts[c.id] ?? savedDrafts[c.id];
                            return (
                                <tr
                                    key={c.id}
                                    onMouseEnter={() => setHoveredRow(c.id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    style={{
                                        background: hoveredRow === c.id ? '#EEF1FB' : i % 2 === 0 ? '#fff' : '#F9FAFF',
                                        borderTop: '1px solid #EEF0F8',
                                    }}
                                >
                                    <td
                                        style={{
                                            padding: '10px 12px',
                                            verticalAlign: 'top',
                                            borderLeft: c.requiredForCompliance ? '3px solid #0B1157' : '3px solid transparent',
                                        }}
                                    >
                                        <div className="d-flex align-items-center flex-wrap gap-2">
                                            <div className="fw-semibold" style={{ fontSize: 13.5, color: '#0B1157' }}>
                                                {c.title} <span className="text-muted fw-normal">({c.article})</span>
                                            </div>
                                            {c.requiredForCompliance && (
                                                <span
                                                    style={{
                                                        fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
                                                        padding: '2px 8px', borderRadius: 999,
                                                        backgroundColor: '#0B1157', color: '#4DFFFF',
                                                    }}
                                                >
                                                    Povinné
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => toggleExpanded(c.id)}
                                            className="d-inline-flex align-items-center gap-1 mt-1"
                                            style={{
                                                border: 'none', background: 'none', padding: 0,
                                                fontSize: 12, fontWeight: 600, color: '#0d6efd', cursor: 'pointer',
                                            }}
                                        >
                                            {expanded[c.id] ? 'Skryť znenie požiadavky' : 'Zobraziť znenie požiadavky'}
                                            <Chevron open={!!expanded[c.id]} />
                                        </button>
                                        {expanded[c.id] && (
                                            <div className="text-muted small mt-2" style={{ lineHeight: 1.5, maxWidth: 420 }}>
                                                {c.description}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                        <Form.Select
                                            size="sm"
                                            value={draft.status}
                                            onChange={e => updateDraft(c.id, 'status', e.target.value)}
                                        >
                                            {statusOptions.map(o => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </Form.Select>
                                    </td>
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                        <Form.Select
                                            size="sm"
                                            value={draft.assessmentType || ''}
                                            onChange={e => updateDraft(c.id, 'assessmentType', e.target.value)}
                                        >
                                            <option value="">—</option>
                                            <option value="INTERNAL">Interné</option>
                                            <option value="EXTERNAL">Externé</option>
                                        </Form.Select>
                                    </td>
                                    <td style={{ padding: '10px 12px', verticalAlign: 'top' }}>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            size="sm"
                                            value={getNoteForLang(c.id)}
                                            onChange={e => setNoteForLang(c.id, e.target.value)}
                                            style={activeLang !== BASE_LANGUAGE ? { fontStyle: getNoteForLang(c.id) ? 'normal' : 'italic' } : undefined}
                                            placeholder={activeLang !== BASE_LANGUAGE && !getNoteForLang(c.id) ? 'Preklad...' : undefined}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-end mt-3">
                <Button
                    className="btn-custom-small"
                    disabled={!isDirty || saving}
                    onClick={handleSaveAll}
                >
                    {saving ? 'Ukladá sa...' : 'Uložiť zmeny'}
                </Button>
            </div>

            <div className="mt-4">
                <button
                    type="button"
                    onClick={() => setIsHistoryOpen(o => !o)}
                    className="d-inline-flex align-items-center gap-2"
                    style={{
                        border: 'none', background: 'none', padding: 0,
                        fontSize: 13, fontWeight: 600, color: '#0B1157', cursor: 'pointer',
                    }}
                >
                    História posúdenia {history.length > 0 && `(${history.length})`}
                    <Chevron open={isHistoryOpen} />
                </button>

                {isHistoryOpen && (
                    <div className="mt-2">
                        {history.length === 0 ? (
                            <p className="text-muted small">Zatiaľ žiadne zmeny stavu.</p>
                        ) : (
                            history.map(entry => (
                                <HistoryEntry key={entry._id} entry={entry} />
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConformityAssessmentTab;
